import moment, {Moment} from 'moment-tz';
import {createSelector} from 'reselect';

import {
  dataRangeTemplatesMap,
  findIntervalType,
  NamedRangeType,
} from 'app/general/dates/date-range';

import type {TaskDashboardLocationParamsType} from 'app/tasks/types';

export const DEFAULT_INTERVAL_FILTER = dataRangeTemplatesMap['this-week'];

export const getStartEndIntervalFromParams = (
  {
    start,
    end,
    interval,
    template_id,
  }: {
    start?: string;
    end?: string;
    interval?: TaskDashboardLocationParamsType['interval'];
    template_id?: string;
  },
  userTz: string
): {
  due__gte?: string;
  due__lte?: string;
} => {
  if (start != null && end != null) {
    return {
      due__gte: start,
      due__lte: end,
    };
  }

  if (interval != null) {
    const template = dataRangeTemplatesMap[interval];

    if (template != null) {
      const {start, end} = template.build(userTz);
      return {
        due__gte: start,
        due__lte: end,
      };
    }
  }

  if (template_id != null) {
    return Object.freeze({});
  }

  const range = DEFAULT_INTERVAL_FILTER.build(userTz);
  return {
    due__gte: range.start,
    due__lte: range.end,
  };
};

export type DueFilterIntervalData = NamedRangeType & {
  start: Moment;
  end: Moment;
};

export const getDueFilterIntervalData = createSelector<
  TaskDashboardLocationParamsType,
  DueFilterIntervalData | void,
  TaskDashboardLocationParamsType['interval'],
  TaskDashboardLocationParamsType['start'],
  TaskDashboardLocationParamsType['end'],
  string
>(
  (params) => params.interval,
  (params) => params.start,
  (params) => params.end,
  (params, tz) => tz,
  (interval, start, end, tz: string) => {
    const {due__gte, due__lte} = getStartEndIntervalFromParams(
      {
        interval,
        start,
        end,
      },
      tz
    );

    if (due__gte != null && due__lte != null) {
      const mStart = moment.tz(due__gte, tz);
      const mEnd = moment.tz(due__lte, tz);
      const interval = findIntervalType(mStart, mEnd);
      return {...interval, start: mStart, end: mEnd};
    }
  }
);
